import { useEffect, useState } from "react";

import { RoleInfoHeaderComponent } from "@/Administration/Components/Roles/RoleInfoHeaderComponent";
import { RoleInfoTabMenuComponent } from "@/Administration/Components/Roles/RoleInfoTabMenuComponent";
import { PermissionDependencyResolver } from "@/Administration/Helpers/PermissionDependencyResolver";
import { RoleModel } from "@/Administration/Models/Role/RoleModel";
import { RoleInfoComponentTabs } from "@/Administration/Models/UI/Roles/RoleInfoComponentTabs";
import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FormMode } from "@/Common/Models/UI/Enums";
import { PermissionSettingsSectionComponent } from "../Users/PermissionSettings/PermissionSettingsSectionComponent";
import { RoleDetailsEditComponent } from "./RoleDetailsEditComponent";

import styles from "./RoleManagementComponent.module.scss";

interface IRoleInfoComponentProps {
    role: RoleModel;
    formMode: FormMode;
    onSaveButtonClick: (role: RoleModel) => void;
    onModifyButtonClick: () => void;
    onDeleteButtonClick: (role: RoleModel, deleteAllUsers: boolean) => void;
    onCancelButtonClick: () => void;
}

function RoleInfoComponent({
    role,
    formMode,
    onSaveButtonClick,
    onModifyButtonClick,
    onDeleteButtonClick,
    onCancelButtonClick
}: Readonly<IRoleInfoComponentProps>): JSX.Element {
    const [currentTab, setCurrentTab] = useState(RoleInfoComponentTabs.GeneralInformation);
    const [roleChanges, setRoleChanges] = useState<RoleModel>(null);

    const isBeingEdited = formMode === FormMode.Edit || formMode === FormMode.New;

    const { getText } = useTranslation();

    useEffect(
        () => {
            if (formMode === FormMode.View)
                setRoleChanges(null);
            else if (formMode === FormMode.Edit)
                setRoleChanges({ ...role });
            else
                setRoleChanges(new RoleModel());
        },
        [formMode, role]
    );

    const tryDelete = (): void => {
        const hasUnsavedChanges: boolean = isBeingEdited && roleChanges?.UserIds.length == 0;

        if (!hasUnsavedChanges && role?.UserIds?.length > 0) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: [getText("RoleBasedAccessControl_DeleteRoleAssignedUsersWarningLabel"), getText("RoleBasedAccessControl_DeleteRoleUnassignUsersInfoLabel")],
                showCancel: true,
                showOk: false
            });

            return;
        }

        if (role)
            onDeleteButtonClick(role, hasUnsavedChanges);
    };

    const permissionsCheckChange = (isChecked: boolean, permissions: Permission[]): void => {
        const currentRole = isBeingEdited ? roleChanges : role;

        currentRole.PermissionIds = PermissionDependencyResolver.ResolvePermissionChangesAndDependencies(currentRole.PermissionIds, permissions, isChecked);
        setRoleChanges({ ...currentRole });
    };

    const currentData = isBeingEdited ? roleChanges : role;

    if (!currentData)
        return <></>;

    return (
        <div className={styles.roleInfoComponent} data-cy="roleInformationComponent">

            <RoleInfoHeaderComponent roleDetails={currentData} />

            <RoleInfoTabMenuComponent
                onChange={clickedTab => setCurrentTab(clickedTab)}
                currentTab={currentTab}
            />

            {currentTab === RoleInfoComponentTabs.GeneralInformation && (
                <div className={styles.roleInfoTab}>
                    <RoleDetailsEditComponent
                        role={currentData}
                        onChange={(roleChanges: RoleModel) => setRoleChanges(roleChanges)}
                        disableEditing={!isBeingEdited}
                    />
                </div>
            )}

            {currentTab === RoleInfoComponentTabs.Permissions && (
                <div className={styles.roleInfoTab}>
                    <PermissionSettingsSectionComponent
                        disabled={!isBeingEdited}
                        onPermissionCheckChange={permissionsCheckChange}
                        permissionIds={currentData.PermissionIds}
                        containerClassName={styles.permissionSettingsSectionComponentContainer}
                    />
                </div>
            )}

            <div className={styles.footer}>
                {!isBeingEdited && role?.Id && (
                    <div className={styles.buttonGroup}>
                        <button
                            className="btn btn-primary"
                            onClick={onModifyButtonClick}
                        >
                            {getText("ModifyLabel")}
                        </button>
                    </div>
                )}
                {isBeingEdited && (
                    <div className={styles.footerButtons}>
                        <div className={styles.buttonGroup}>
                            {(formMode !== FormMode.New) && (
                                <button
                                    className="btn btn-danger"
                                    onClick={tryDelete}
                                >
                                    {getText("DeleteLabel")}
                                </button>
                            )}
                        </div>
                        <div className={styles.buttonGroup}>
                            <button
                                className="btn btn-default"
                                onClick={onCancelButtonClick}
                            >
                                {getText("CancelLabel")}
                            </button>
                            <button
                                data-cy="saveRoleButton"
                                className="btn btn-primary"
                                disabled={!currentData?.Name}
                                onClick={() => onSaveButtonClick(roleChanges)}
                            >
                                {getText("SaveLabel")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export { RoleInfoComponent };
